import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import NetherlandsHero from './components/NetherlandsHero';
import NetherlandsContractContents from './components/NetherlandsContractContents';
import NetherlandsExampleClauses from './components/NetherlandsExampleClauses';
import NetherlandsFaq from './components/NetherlandsFaq';
import NetherlandsInternalLinks from './components/NetherlandsInternalLinks';

const faqs = [
  {
    question: 'Wat is een freelance contract voor ZZP\'ers?',
    answer: 'Een freelance contract voor ZZP\'ers is een juridisch bindende overeenkomst tussen een ZZP\'er en een opdrachtgever, conform het Nederlands Burgerlijk Wetboek.',
  },
  {
    question: 'Heb ik een KvK-nummer nodig als ZZP\'er in Nederland?',
    answer: 'Ja, als ZZP\'er in Nederland ben je verplicht om je in te schrijven bij de Kamer van Koophandel (KvK) en een KvK-nummer te verkrijgen voor al je facturen en contracten.',
  },
  {
    question: 'Welk recht geldt voor ZZP contracten in Nederland?',
    answer: 'ZZP contracten in Nederland worden beheerst door het Burgerlijk Wetboek (BW) en de Wet DBA. Voor btw-aspecten geldt de Wet op de Omzetbelasting 1968.',
  },
  {
    question: 'Is een ZZP contract verplicht in Nederland?',
    answer: 'Een schriftelijk ZZP contract is niet wettelijk verplicht, maar sterk aanbevolen om afspraken te bewijzen bij een geschil en voor de Wet DBA-beoordeling.',
  },
  {
    question: 'Kan ik een gratis ZZP contract template gebruiken voor Nederland?',
    answer: 'Ja, met Pactorae genereer je gratis een professioneel ZZP contract afgestemd op de Nederlandse wetgeving, inclusief KvK-nummer en btw-nummer.',
  },
];

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://pactorae.com/freelance-contract-netherlands',
      url: 'https://pactorae.com/freelance-contract-netherlands',
      name: 'Freelance Contract Template Nederland | Pactorae',
      description:
        'Gratis freelance contract template voor ZZP\'ers en freelancers in Nederland. Met KvK-nummer, btw-nummer en conform Nederlands recht. Genereer direct online.',
      inLanguage: 'nl',
      isPartOf: { '@type': 'WebSite', url: 'https://pactorae.com', name: 'Pactorae' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs?.map((faq) => ({
        '@type': 'Question',
        name: faq?.question,
        acceptedAnswer: { '@type': 'Answer', text: faq?.answer },
      })),
    },
  ],
};

const FreelanceContractNetherlandsPage = () => (
  <>
    <Helmet>
      <html lang="nl" />
      <title>Freelance Contract Template Nederland | ZZP Contract Generator | Pactorae</title>
      <meta
        name="description"
        content="Gratis freelance contract template voor ZZP'ers en freelancers in Nederland. Met KvK-nummer, btw-nummer en conform Nederlands recht. Genereer in 30 seconden online."
      />
      <meta
        name="keywords"
        content="freelance contract Nederland, zzp contract template, KvK contract generator, zzp overeenkomst Nederland, freelance overeenkomst Nederland, contract generator Nederland, zelfstandige contract Nederland"
      />
      <link rel="canonical" href="https://pactorae.com/freelance-contract-netherlands" />
      <meta name="robots" content="index, follow" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://pactorae.com/freelance-contract-netherlands" />
      <meta property="og:title" content="Freelance Contract Template Nederland | ZZP Contract Generator | Pactorae" />
      <meta
        property="og:description"
        content="Gratis freelance contract template voor ZZP'ers en freelancers in Nederland. Met KvK-nummer, btw-nummer en conform Nederlands recht."
      />
      <meta property="og:site_name" content="Pactorae" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Freelance Contract Template Nederland | ZZP Contract Generator | Pactorae" />
      <meta
        name="twitter:description"
        content="Gratis freelance contract template voor ZZP'ers in Nederland. Met KvK-nummer, btw-nummer en conform Nederlands recht."
      />
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
    </Helmet>

    <NetherlandsHero />

    <article className="max-w-4xl mx-auto px-6 py-16">
      {/* Intro */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-[#2F4E6F] mb-4">Freelance contract opstellen als ZZP'er in Nederland</h2>
        <p className="text-gray-700 text-lg leading-relaxed mb-4">
          Als ZZP'er (zelfstandige zonder personeel) in Nederland is een goed contract onmisbaar. Een professioneel freelance contract beschermt je bij onbetaalde facturen, scope creep en juridische conflicten. Pactorae genereert in 30 seconden een contract op maat voor de Nederlandse markt.
        </p>
        <p className="text-gray-700 leading-relaxed">
          Onze generator houdt rekening met Nederlandse specifieke vereisten zoals jouw <strong>KvK-nummer</strong>, <strong>btw-nummer</strong> en de toepasselijkheid van het <strong>Nederlands Burgerlijk Wetboek</strong> en de <strong>Wet DBA</strong>. Zo ben je zeker dat jouw contract juridisch correct is voor de Nederlandse markt.
        </p>
      </section>

      <NetherlandsContractContents />
      <NetherlandsExampleClauses />

      {/* CTA */}
      <section className="mb-12 bg-gradient-to-r from-[#2F4E6F] to-[#1e3550] rounded-2xl p-8 text-center text-white">
        <h2 className="text-2xl font-bold mb-3">Klaar om jouw ZZP contract te genereren?</h2>
        <p className="text-blue-100 mb-6 max-w-xl mx-auto">
          Vul jouw KvK-nummer en btw-nummer in en genereer in 30 seconden een professioneel contract conform het Nederlands recht.
        </p>
        <Link
          to="/contract-generator"
          className="inline-block bg-[#FF8C00] hover:bg-[#e07b00] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors duration-200 shadow-lg"
        >
          Genereer jouw contract gratis
        </Link>
      </section>

      <NetherlandsFaq />
      <NetherlandsInternalLinks />
    </article>
  </>
);

export default FreelanceContractNetherlandsPage;
